import { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { X, PaperPlaneTilt, Warning, GearSix } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import MaterialSelector from '@/pages/workflows/components/config/MaterialSelector'
import { contactsApi } from '@/lib/api'
import { useSenderEmailStore } from '@/stores/senderEmailStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { cn } from '@/lib/utils'

interface ComposeContactRef {
  id: number
  email?: string | null
  full_name?: string | null
  first_name?: string | null
}

interface ComposeEmailModalProps {
  isOpen: boolean
  onClose: () => void
  contact: ComposeContactRef
  prefillSubject?: string
  prefillBody?: string
}

const LABEL = 'block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]'

export default function ComposeEmailModal({
  isOpen,
  onClose,
  contact,
  prefillSubject = '',
  prefillBody = '',
}: ComposeEmailModalProps) {
  const { verifiedSenders, fetchVerifiedSenders } = useSenderEmailStore()
  const navigate = useNavigate()

  const [senderId, setSenderId] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [materialIds, setMaterialIds] = useState<number[]>([])
  const [includeSignature, setIncludeSignature] = useState(true)
  const [sending, setSending] = useState(false)

  // (Re)initialise every time the modal opens.
  useEffect(() => {
    if (!isOpen) return
    fetchVerifiedSenders()
    setSubject(prefillSubject)
    setBody(prefillBody)
    setMaterialIds([])
    setIncludeSignature(true)
    setSending(false)
  }, [isOpen])

  // Default the sender to the account default (or first verified) once loaded.
  useEffect(() => {
    if (!isOpen || senderId) return
    if (verifiedSenders.length > 0) {
      const def = verifiedSenders.find(s => s.is_default) || verifiedSenders[0]
      setSenderId(String(def.id))
    }
  }, [isOpen, verifiedSenders, senderId])

  const selectedSender = useMemo(
    () => verifiedSenders.find(s => String(s.id) === senderId),
    [verifiedSenders, senderId],
  )

  const senderOptions = useMemo(
    () => verifiedSenders.map(s => ({
      value: String(s.id),
      label: `${s.email_address}${s.is_default ? '  (default)' : ''}`,
    })),
    [verifiedSenders],
  )

  const signaturePreview = useMemo(
    () => (selectedSender?.signature_html || '').replace(/\n/g, '<br>'),
    [selectedSender],
  )

  if (!isOpen) return null

  const recipientName = contact.full_name || contact.email || 'this contact'
  const noSenders = verifiedSenders.length === 0
  const canSend =
    !sending && !!senderId && !!contact.email && subject.trim().length > 0 && body.trim().length > 0

  const handleSend = async () => {
    if (!canSend) return
    setSending(true)
    try {
      await contactsApi.sendEmail(contact.id, {
        subject: subject.trim(),
        body,
        sender_email_id: Number(senderId),
        material_ids: materialIds,
        include_signature: includeSignature,
      })
      toast.success(`Email sent to ${contact.email}`)
      onClose()
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to send the email.'
      toast.error(typeof detail === 'string' ? detail : 'Failed to send the email.')
    } finally {
      setSending(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-[100]">
      <div className="bg-card border border-border/60 rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-[#5e6ad2]/10 flex items-center justify-center">
              <PaperPlaneTilt className="w-5 h-5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
            </div>
            <div>
              <h3 className="text-base font-emphasis text-foreground">Send email</h3>
              <p className="text-xs text-muted-foreground">to {recipientName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {noSenders ? (
            <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3">
              <Warning className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" weight="fill" />
              <div>
                <p className="text-[13px] font-medium text-foreground">No verified sender email</p>
                <p className="text-[12px] text-muted-foreground mt-0.5">
                  Add and verify a sender address in Settings → Email accounts before sending.
                </p>
              </div>
            </div>
          ) : (
            <div>
              <label className={LABEL}>From</label>
              <Select
                value={senderId}
                onChange={setSenderId}
                options={senderOptions}
                placeholder="Select a sender…"
                ariaLabel="Sender email"
              />
            </div>
          )}

          <div>
            <label className={LABEL}>To</label>
            <p className="text-[13px] text-foreground dark:text-[#f7f8f8] px-3 py-2 rounded-md bg-muted/40 dark:bg-white/[0.03] border border-border/50 dark:border-white/[0.06]">
              {contact.email || <span className="text-amber-600 dark:text-amber-400">No email address on file</span>}
            </p>
          </div>

          <div>
            <Input
              label="Subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Subject line"
            />
          </div>

          <div>
            <label className={LABEL}>Message</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={9}
              placeholder="Write your message…"
              className="w-full px-3 py-2 bg-background dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.08] hover:border-border/80 dark:hover:border-white/[0.14] rounded-md text-[13px] leading-relaxed text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d] focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 transition-colors duration-150 resize-y"
            />
          </div>

          {/* Attachments (marketing materials) */}
          <MaterialSelector selectedIds={materialIds} onChange={setMaterialIds} />

          {/* Signature */}
          {selectedSender?.signature_html ? (
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeSignature}
                  onChange={e => setIncludeSignature(e.target.checked)}
                  className="h-3.5 w-3.5 accent-[#5e6ad2]"
                />
                <span className="text-[13px] text-foreground dark:text-[#f7f8f8]">Append my signature</span>
              </label>
              {includeSignature && (
                <div
                  className="text-[12px] text-muted-foreground dark:text-[#8a8f98] px-3 py-2 rounded-md bg-muted/30 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.06]"
                  dangerouslySetInnerHTML={{ __html: signaturePreview }}
                />
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-border/60">
          {noSenders ? (
            <button
              onClick={() => {
                useSettingsStore.getState().setActiveSection('email-accounts')
                onClose()
                navigate('/settings')
              }}
              className="inline-flex items-center gap-1.5 text-[13px] text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline underline-offset-4"
            >
              <GearSix className="h-3.5 w-3.5" /> Go to Settings
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleSend}
              loading={sending}
              disabled={!canSend}
              icon={<PaperPlaneTilt className={cn('h-3.5 w-3.5')} weight="fill" />}
            >
              Send email
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
